import React from "react";
import MenuList from "@mui/material/MenuList";
import MenuItem from "@mui/material/MenuItem";
import Collapse from "@mui/material/Collapse";
import SFTypography from "../../../typography";
import { SFStack } from "../../../grid";
import { SFExpandLessIcon, SFExpandMoreIcon } from "../../../icons";
import getStyled from "../../../../hooks/getStyled";
import PropTypes from "prop-types";

const { styled } = getStyled();

const MenuItemStyled = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== "disableBorder",
})(({ theme, disableBorder = false }) => ({
  width: "100%",
  minHeight: "36px",
  padding: "6px 10px",
  ...(!disableBorder && {
    borderBottom: `1px solid ${theme.palette.grey200.main}`,
  }),
  "&.Mui-selected": {
    backgroundColor: theme.palette.grey100.main,
  },
}));

const SFStackStyled = styled(SFStack)(({ theme }) => ({
  width: "100%",
  textWrap: "wrap",
  "& .MuiSvgIcon-root": {
    width: "20px",
    height: "20px",
  },
}));

const SubmenuItem = ({
  label,
  onClick,
  disableBorder,
  selectedItems,
  disabled,
}) => {
  return (
    <MenuItemStyled
      onClick={onClick}
      disableBorder={disableBorder}
      selected={selectedItems.includes(label)}
      disabled={disabled}
    >
      <SFStack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{ width: "100%", textWrap: "wrap" }}
      >
        <span style={{ width: "20px", height: "20px" }} />
        <SFTypography variant="body2">{label}</SFTypography>
      </SFStack>
    </MenuItemStyled>
  );
};

const Submenu = ({
  icon,
  label,
  onClick,
  items,
  disableBorder,
  selectedItems,
  disabled,
}) => {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <MenuItemStyled
        onClick={() => {
          onClick();
          setOpen(!open);
        }}
        // Disable border if the submenu is open or if the parent item has a disabled border
        disableBorder={open || disableBorder}
        selected={selectedItems.includes(label)}
        disabled={disabled}
      >
        <SFStackStyled
          direction="row"
          spacing={0.5}
          alignItems="center"
          justifyContent="space-between"
        >
          <SFStack direction="row" spacing={0.5} alignItems="center">
            {icon}
            <SFTypography variant="body2">{label}</SFTypography>
          </SFStack>
          {open ? (
            <SFExpandLessIcon color="grey350" />
          ) : (
            <SFExpandMoreIcon color="grey350" />
          )}
        </SFStackStyled>
      </MenuItemStyled>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <MenuList
          autoFocusItem={open}
          id="composition-menu"
          aria-labelledby="composition-button"
          disablePadding
        >
          {items.map((item, index) => (
            <SubmenuItem
              key={index}
              label={item.label}
              onClick={item.onClick}
              disableBorder={
                // Disable border for the inbetween items
                index < items.length - 1 ||
                // Disable border for the last item if the parent item has a disabled border
                (index === items.length - 1 && disableBorder)
              }
              selectedItems={selectedItems}
              disabled={item.disabled}
            />
          ))}
        </MenuList>
      </Collapse>
    </>
  );
};

const Item = ({ icon, label, onClick, disableBorder, disabled }) => {
  return (
    <MenuItemStyled
      onClick={onClick}
      disableBorder={disableBorder}
      disabled={disabled}
    >
      <SFStackStyled direction="row" spacing={0.5} alignItems="center">
        {icon}
        <SFTypography variant="body2">{label}</SFTypography>
      </SFStackStyled>
    </MenuItemStyled>
  );
};

const OverflowMenuItem = ({
  actionDef,
  disableBorder,
  closeMenu,
  selectedItems,
}) => {
  const { children = [] } = actionDef;

  if (Array.isArray(children) && children.length === 0) {
    // If there are no children, render a regular item
    return (
      <Item
        icon={actionDef.iconProps?.icon}
        label={actionDef.iconProps?.overflowLabel || actionDef.name}
        onClick={() => {
          actionDef.onClick();
          closeMenu();
        }}
        disableBorder={disableBorder}
        disabled={actionDef.disabled}
      />
    );
  } else {
    // If there are children, render a submenu
    return (
      <Submenu
        icon={actionDef.iconProps?.icon}
        label={actionDef.iconProps?.overflowLabel || actionDef.name}
        onClick={() => {
          actionDef.onClick();
        }}
        items={actionDef.children.map((child) => ({
          label: child.label,
          onClick: () => {
            child.onClick();
            closeMenu();
          },
          disabled: child.disabled,
        }))}
        disableBorder={disableBorder}
        selectedItems={selectedItems}
        disabled={actionDef.disabled}
      />
    );
  }
};

export default OverflowMenuItem;

OverflowMenuItem.propTypes = {
  actionDef: PropTypes.shape({
    // The name of the action
    name: PropTypes.string.isRequired,
    // The function to call when the action is clicked
    onClick: PropTypes.func.isRequired,
    // Props specific to the icon type action
    iconProps: PropTypes.shape({
      // The icon to display
      icon: PropTypes.node,
      // The label of the icon when it is in the overflow menu
      overflowLabel: PropTypes.string,
    }),
    // The children of the action
    children: PropTypes.arrayOf(
      PropTypes.shape({
        // The name of the child action
        name: PropTypes.string.isRequired,
        // The label of the child action
        label: PropTypes.string.isRequired,
        // The function to call when the child action is clicked
        onClick: PropTypes.func.isRequired,
      })
    ),
  }),
  // Whether to disable the border on the item
  disableBorder: PropTypes.bool,
  // The close handler of the menu
  closeMenu: PropTypes.func,
};
